import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import CustomFormInput from '../components/forms/CustomFormInput';
import {
    pickTravelColor,
    TRAVEL_COLOR_HUES,
    TRAVEL_COLOR_NAMES,
    FAMILY_SCHEMES,
    DEFAULT_VETO_DEGREES,
    DEFAULT_PROXIMITY_DAYS,
} from '../utils/availabilityHelpers';
import './FormInputShowcase.css';

/**
 * FormInputShowcase Component
 *
 * Comprehensive demonstration page showing all CustomFormInput variations:
 * - Standard checkboxes (all color variants)
 * - Standard radio buttons (all color variants)
 * - Pill-style checkboxes (all color variants)
 * - Pill-style radio buttons (all color variants)
 * - React Hook Form integration examples
 * - Error states and validation
 * - Disabled states
 * - Hint text examples
 */
const FormInputShowcase = () => {
    // ========================================
    // SECTION 1: Uncontrolled State Examples
    // ========================================
    const [standardCheckboxes, setStandardCheckboxes] = useState({
        turquoise: false,
        purple: false,
        purpleLight: false,
        orange: false,
        green: false,
    });

    const [pillRadio, setPillRadio] = useState('30');

    // ========================================
    // SECTION 2: React Hook Form Example
    // ========================================
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        defaultValues: {
            acceptTerms: false,
            newsletter: false,
            lessonDuration: '60',
            paymentMethod: 'stripe',
            features: [],
        },
    });

    const onSubmit = (data) => {
        console.log('Form submitted:', data);
        alert(`Form submitted!\n\n${JSON.stringify(data, null, 2)}`);
    };

    // Watch form values for display
    const formValues = watch();

    return (
        <div className="showcase-container">
            {/* Header */}
            <div className="showcase-header">
                <h1>Custom Form Input Showcase</h1>
                <p className="showcase-subtitle">
                    Comprehensive demonstration of CustomFormInput component with all variants and React Hook Form
                    integration
                </p>
            </div>

            {/* ========================================
                SECTION 1: STANDARD CHECKBOXES
                ======================================== */}
            <section className="showcase-section">
                <div className="section-header">
                    <h2>Standard Checkboxes</h2>
                    <p className="section-description">
                        Traditional checkbox style with visible native input. All color variants available.
                    </p>
                </div>

                <div className="demo-grid">
                    {/* Turquoise */}
                    <div className="demo-card">
                        <h3>Turquoise (Default)</h3>
                        <p className="demo-description">Most common, general-purpose use</p>
                        <CustomFormInput
                            type="checkbox"
                            variant="turquoise"
                            layout="standard"
                            label="Enable notifications"
                            checked={standardCheckboxes.turquoise}
                            onChange={(e) =>
                                setStandardCheckboxes((prev) => ({ ...prev, turquoise: e.target.checked }))
                            }
                            hint="You'll receive email updates about your account"
                        />
                        <code className="demo-code">variant="turquoise"</code>
                    </div>

                    {/* Purple */}
                    <div className="demo-card">
                        <h3>Purple (Brand Dark)</h3>
                        <p className="demo-description">Brand emphasis, important selections</p>
                        <CustomFormInput
                            type="checkbox"
                            variant="purple"
                            layout="standard"
                            label="Feature highlights"
                            checked={standardCheckboxes.purple}
                            onChange={(e) => setStandardCheckboxes((prev) => ({ ...prev, purple: e.target.checked }))}
                        />
                        <code className="demo-code">variant="purple"</code>
                    </div>

                    {/* Purple Light */}
                    <div className="demo-card">
                        <h3>Purple Light (Brand Accent)</h3>
                        <p className="demo-description">Lighter brand accent, softer emphasis</p>
                        <CustomFormInput
                            type="checkbox"
                            variant="purple-light"
                            layout="standard"
                            label="Enable extras"
                            checked={standardCheckboxes.purpleLight}
                            onChange={(e) => setStandardCheckboxes((prev) => ({ ...prev, purpleLight: e.target.checked }))}
                        />
                        <code className="demo-code">variant="purple-light"</code>
                    </div>

                    {/* Orange */}
                    <div className="demo-card">
                        <h3>Orange (Warning)</h3>
                        <p className="demo-description">Warnings, important actions</p>
                        <CustomFormInput
                            type="checkbox"
                            variant="orange"
                            layout="standard"
                            label="Accept risks"
                            checked={standardCheckboxes.orange}
                            onChange={(e) => setStandardCheckboxes((prev) => ({ ...prev, orange: e.target.checked }))}
                        />
                        <code className="demo-code">variant="orange"</code>
                    </div>

                    {/* Green */}
                    <div className="demo-card">
                        <h3>Green (Success)</h3>
                        <p className="demo-description">Confirmations, approvals</p>
                        <CustomFormInput
                            type="checkbox"
                            variant="green"
                            layout="standard"
                            label="Mark as completed"
                            checked={standardCheckboxes.green}
                            onChange={(e) => setStandardCheckboxes((prev) => ({ ...prev, green: e.target.checked }))}
                        />
                        <code className="demo-code">variant="green"</code>
                    </div>
                </div>

                {/* State display */}
                <div className="state-display">
                    <strong>Current State:</strong>
                    <pre>{JSON.stringify(standardCheckboxes, null, 2)}</pre>
                </div>
            </section>

            {/* ========================================
                SECTION 2: PILL-STYLE RADIO BUTTONS
                ======================================== */}
            <section className="showcase-section">
                <div className="section-header">
                    <h2>Pill-Style Radio Buttons</h2>
                    <p className="section-description">
                        Button-like appearance, perfect for horizontal option selection (like lesson duration selector).
                    </p>
                </div>

                <div className="demo-grid">
                    {/* Turquoise Pills */}
                    <div className="demo-card demo-card-wide">
                        <h3>Turquoise Pills</h3>
                        <p className="demo-description">Lesson duration selector pattern</p>
                        <div className="pill-group">
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                label="30 min"
                                name="duration-turquoise"
                                value="30"
                                checked={pillRadio === '30'}
                                onChange={(e) => setPillRadio(e.target.value)}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                label="60 min"
                                name="duration-turquoise"
                                value="60"
                                checked={pillRadio === '60'}
                                onChange={(e) => setPillRadio(e.target.value)}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                label="90 min"
                                name="duration-turquoise"
                                value="90"
                                checked={pillRadio === '90'}
                                onChange={(e) => setPillRadio(e.target.value)}
                            />
                        </div>
                        <code className="demo-code">layout="pill" variant="turquoise"</code>
                    </div>

                    {/* Purple Pills */}
                    <div className="demo-card demo-card-wide">
                        <h3>Purple Pills</h3>
                        <p className="demo-description">Brand-themed option selector</p>
                        <div className="pill-group">
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                layout="pill"
                                label="Basic"
                                name="plan-purple"
                                value="basic"
                            />
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                layout="pill"
                                label="Pro"
                                name="plan-purple"
                                value="pro"
                                defaultChecked
                            />
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                layout="pill"
                                label="Enterprise"
                                name="plan-purple"
                                value="enterprise"
                            />
                        </div>
                        <code className="demo-code">layout="pill" variant="purple"</code>
                    </div>

                    {/* Orange Pills */}
                    <div className="demo-card demo-card-wide">
                        <h3>Orange Pills</h3>
                        <p className="demo-description">Priority level selector</p>
                        <div className="pill-group">
                            <CustomFormInput
                                type="radio"
                                variant="orange"
                                layout="pill"
                                label="Low"
                                name="priority-orange"
                                value="low"
                                defaultChecked
                            />
                            <CustomFormInput
                                type="radio"
                                variant="orange"
                                layout="pill"
                                label="Medium"
                                name="priority-orange"
                                value="medium"
                            />
                            <CustomFormInput
                                type="radio"
                                variant="orange"
                                layout="pill"
                                label="High"
                                name="priority-orange"
                                value="high"
                            />
                        </div>
                        <code className="demo-code">layout="pill" variant="orange"</code>
                    </div>

                    {/* Green Pills */}
                    <div className="demo-card demo-card-wide">
                        <h3>Green Pills</h3>
                        <p className="demo-description">Status confirmation selector</p>
                        <div className="pill-group">
                            <CustomFormInput
                                type="radio"
                                variant="green"
                                layout="pill"
                                label="Pending"
                                name="status-green"
                                value="pending"
                            />
                            <CustomFormInput
                                type="radio"
                                variant="green"
                                layout="pill"
                                label="Approved"
                                name="status-green"
                                value="approved"
                                defaultChecked
                            />
                            <CustomFormInput
                                type="radio"
                                variant="green"
                                layout="pill"
                                label="Completed"
                                name="status-green"
                                value="completed"
                            />
                        </div>
                        <code className="demo-code">layout="pill" variant="green"</code>
                    </div>
                </div>
            </section>

            {/* ========================================
                SECTION 3: STANDARD RADIO BUTTONS
                ======================================== */}
            <section className="showcase-section">
                <div className="section-header">
                    <h2>Standard Radio Buttons</h2>
                    <p className="section-description">Traditional radio button style for payment methods, settings, etc.</p>
                </div>

                <div className="demo-grid">
                    <div className="demo-card demo-card-wide">
                        <h3>Payment Methods (Turquoise)</h3>
                        <CustomFormInput
                            type="radio"
                            variant="turquoise"
                            layout="standard"
                            label="Credit/Debit Card"
                            name="payment-standard"
                            value="card"
                            defaultChecked
                        />
                        <CustomFormInput
                            type="radio"
                            variant="turquoise"
                            layout="standard"
                            label="PayPal"
                            name="payment-standard"
                            value="paypal"
                        />
                        <CustomFormInput
                            type="radio"
                            variant="turquoise"
                            layout="standard"
                            label="Bank Transfer"
                            name="payment-standard"
                            value="bank"
                        />
                        <code className="demo-code">type="radio" variant="turquoise"</code>
                    </div>

                    <div className="demo-card demo-card-wide">
                        <h3>Notification Frequency (Purple)</h3>
                        <CustomFormInput
                            type="radio"
                            variant="purple"
                            layout="standard"
                            label="Immediately"
                            name="frequency"
                            value="immediate"
                        />
                        <CustomFormInput
                            type="radio"
                            variant="purple"
                            layout="standard"
                            label="Daily Digest"
                            name="frequency"
                            value="daily"
                            defaultChecked
                        />
                        <CustomFormInput
                            type="radio"
                            variant="purple"
                            layout="standard"
                            label="Weekly Summary"
                            name="frequency"
                            value="weekly"
                        />
                        <code className="demo-code">type="radio" variant="purple"</code>
                    </div>
                </div>
            </section>

            {/* ========================================
                SECTION 4: REACT HOOK FORM INTEGRATION
                ======================================== */}
            <section className="showcase-section">
                <div className="section-header">
                    <h2>React Hook Form Integration</h2>
                    <p className="section-description">
                        Full integration with React Hook Form including validation, error handling, and form submission.
                    </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="rhf-demo-form">
                    {/* Checkboxes with validation */}
                    <div className="form-group">
                        <h3>Terms and Conditions</h3>
                        <CustomFormInput
                            type="checkbox"
                            variant="turquoise"
                            layout="standard"
                            label="I accept the terms and conditions"
                            {...register('acceptTerms', {
                                required: 'You must accept the terms to continue',
                            })}
                            error={errors.acceptTerms?.message}
                        />
                        <CustomFormInput
                            type="checkbox"
                            variant="turquoise"
                            layout="standard"
                            label="Subscribe to newsletter (optional)"
                            {...register('newsletter')}
                            hint="Get weekly updates about new dance events"
                        />
                    </div>

                    {/* Pill radio with validation */}
                    <div className="form-group">
                        <h3>Lesson Duration *</h3>
                        <div className="pill-group">
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                label="30 min"
                                value="30"
                                {...register('lessonDuration', {
                                    required: 'Please select a lesson duration',
                                })}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                label="60 min"
                                value="60"
                                {...register('lessonDuration')}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="turquoise"
                                layout="pill"
                                label="90 min"
                                value="90"
                                {...register('lessonDuration')}
                            />
                        </div>
                        {errors.lessonDuration && (
                            <p className="custom-input-error">{errors.lessonDuration.message}</p>
                        )}
                    </div>

                    {/* Standard radio with validation */}
                    <div className="form-group">
                        <h3>Payment Method *</h3>
                        <CustomFormInput
                            type="radio"
                            variant="purple"
                            layout="standard"
                            label="Stripe (Credit/Debit Cards)"
                            value="stripe"
                            {...register('paymentMethod', {
                                required: 'Please select a payment method',
                            })}
                        />
                        <CustomFormInput
                            type="radio"
                            variant="purple"
                            layout="standard"
                            label="PayPal"
                            value="paypal"
                            {...register('paymentMethod')}
                        />
                        <CustomFormInput
                            type="radio"
                            variant="purple"
                            layout="standard"
                            label="Square"
                            value="square"
                            {...register('paymentMethod')}
                        />
                        {errors.paymentMethod && (
                            <p className="custom-input-error">{errors.paymentMethod.message}</p>
                        )}
                    </div>

                    {/* Submit button */}
                    <button type="submit" className="btn-submit">
                        Submit Form
                    </button>

                    {/* Current form values display */}
                    <div className="state-display">
                        <strong>Current Form Values:</strong>
                        <pre>{JSON.stringify(formValues, null, 2)}</pre>
                    </div>
                </form>
            </section>

            {/* ========================================
                SECTION 5: SPECIAL STATES
                ======================================== */}
            <section className="showcase-section">
                <div className="section-header">
                    <h2>Special States</h2>
                    <p className="section-description">Disabled states, errors, and hints demonstration.</p>
                </div>

                <div className="demo-grid">
                    {/* Disabled checkbox */}
                    <div className="demo-card">
                        <h3>Disabled Checkbox</h3>
                        <CustomFormInput
                            type="checkbox"
                            variant="turquoise"
                            layout="standard"
                            label="This option is disabled"
                            disabled={true}
                            checked={true}
                        />
                        <CustomFormInput
                            type="checkbox"
                            variant="turquoise"
                            layout="standard"
                            label="This option is also disabled"
                            disabled={true}
                        />
                        <code className="demo-code">disabled={`{true}`}</code>
                    </div>

                    {/* Error state */}
                    <div className="demo-card">
                        <h3>Error State</h3>
                        <CustomFormInput
                            type="checkbox"
                            variant="turquoise"
                            layout="standard"
                            label="I accept the terms"
                            error="You must accept the terms to continue"
                        />
                        <code className="demo-code">error="Error message"</code>
                    </div>

                    {/* With hint */}
                    <div className="demo-card">
                        <h3>With Hint Text</h3>
                        <CustomFormInput
                            type="checkbox"
                            variant="turquoise"
                            layout="standard"
                            label="Enable two-factor authentication"
                            hint="Adds an extra layer of security to your account"
                        />
                        <code className="demo-code">hint="Helper text"</code>
                    </div>

                    {/* Disabled pill */}
                    <div className="demo-card">
                        <h3>Disabled Pills</h3>
                        <div className="pill-group">
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                layout="pill"
                                label="Option 1"
                                name="disabled-pills"
                                value="1"
                                disabled={true}
                            />
                            <CustomFormInput
                                type="radio"
                                variant="purple"
                                layout="pill"
                                label="Option 2"
                                name="disabled-pills"
                                value="2"
                                disabled={true}
                                defaultChecked
                            />
                        </div>
                        <code className="demo-code">layout="pill" disabled={`{true}`}</code>
                    </div>
                </div>
            </section>

            {/* ========================================
                SECTION 6: CODE EXAMPLES
                ======================================== */}
            <section className="showcase-section">
                <div className="section-header">
                    <h2>Code Examples</h2>
                    <p className="section-description">How to use CustomFormInput in your components.</p>
                </div>

                <div className="code-examples">
                    {/* Example 1: Basic checkbox */}
                    <div className="code-example-block">
                        <h3>Basic Checkbox (Controlled)</h3>
                        <pre className="code-block">{`import CustomFormInput from '../components/forms/CustomFormInput';

const [checked, setChecked] = useState(false);

<CustomFormInput
  type="checkbox"
  variant="turquoise"
  layout="standard"
  label="Enable notifications"
  checked={checked}
  onChange={(e) => setChecked(e.target.checked)}
  hint="You'll receive email updates"
/>`}</pre>
                    </div>

                    {/* Example 2: Pill radio group */}
                    <div className="code-example-block">
                        <h3>Pill-Style Radio Group</h3>
                        <pre className="code-block">{`import CustomFormInput from '../components/forms/CustomFormInput';

const [duration, setDuration] = useState('60');

<div style={{ display: 'flex', gap: '12px' }}>
  <CustomFormInput
    type="radio"
    variant="turquoise"
    layout="pill"
    label="30 min"
    name="duration"
    value="30"
    checked={duration === '30'}
    onChange={(e) => setDuration(e.target.value)}
  />
  <CustomFormInput
    type="radio"
    variant="turquoise"
    layout="pill"
    label="60 min"
    name="duration"
    value="60"
    checked={duration === '60'}
    onChange={(e) => setDuration(e.target.value)}
  />
</div>`}</pre>
                    </div>

                    {/* Example 3: React Hook Form */}
                    <div className="code-example-block">
                        <h3>React Hook Form Integration</h3>
                        <pre className="code-block">{`import { useForm } from 'react-hook-form';
import CustomFormInput from '../components/forms/CustomFormInput';

const { register, handleSubmit, formState: { errors } } = useForm();

<form onSubmit={handleSubmit(onSubmit)}>
  <CustomFormInput
    type="checkbox"
    variant="turquoise"
    layout="standard"
    label="I accept the terms"
    {...register('acceptTerms', {
      required: 'You must accept the terms'
    })}
    error={errors.acceptTerms?.message}
  />

  <button type="submit">Submit</button>
</form>`}</pre>
                    </div>
                </div>
            </section>

            {/* ========================================
                SECTION: TRAVEL COLOUR WHEEL
                ======================================== */}
            <section className="showcase-section">
                <div className="section-header">
                    <h2>Travel Colour Wheel & Algorithm Simulator</h2>
                    <p className="section-description">
                        The 7-colour travel palette plotted on a hue wheel (top), followed by a simulator
                        that runs the production <code>pickTravelColor</code> algorithm against 16 consecutive
                        synthetic trips. Use this to visually verify the LRU + hue-proximity-veto behaviour
                        before committing more travel periods.
                    </p>
                </div>

                <TravelColourWheel />
                <TravelColourSimulator />
            </section>

            {/* Footer */}
            <div className="showcase-footer">
                <p>
                    <strong>Component Location:</strong> <code>src/components/forms/CustomFormInput.js</code>
                </p>
                <p>
                    <strong>CSS Location:</strong> <code>src/components/forms/CustomFormInput.css</code>
                </p>
                <p>
                    <strong>Available Variants:</strong> turquoise (default), purple, orange, green
                </p>
                <p>
                    <strong>Available Styles:</strong> standard, pill
                </p>
            </div>
        </div>
    );
};

// ============================================================
// Travel Colour Wheel — visual reference for the 7-colour travel
// palette used by availability_contexts. Plots each colour on a
// hue wheel at its hue position.
// ============================================================
const EXISTING_TRAVEL_COLOURS = [
    { hex: '#e11d48', name: 'Rose',    hue: 350 },
    { hex: '#84cc16', name: 'Lime',    hue: 84  },
    { hex: '#f59e0b', name: 'Amber',   hue: 38  },
    { hex: '#10b981', name: 'Emerald', hue: 160 },
    { hex: '#06b6d4', name: 'Cyan',    hue: 188 },
    { hex: '#3b82f6', name: 'Blue',    hue: 217 },
    { hex: '#d946ef', name: 'Magenta', hue: 291 },
];

const WHEEL_SIZE = 360;
const WHEEL_CENTER = WHEEL_SIZE / 2;
const WHEEL_OUTER_R = 150;
const WHEEL_INNER_R = 100;

function hueToXY(hue, radius) {
    // SVG: 0° at 3 o'clock by default; we want 0° at 12 o'clock and clockwise.
    const rad = ((hue - 90) * Math.PI) / 180;
    return {
        x: WHEEL_CENTER + radius * Math.cos(rad),
        y: WHEEL_CENTER + radius * Math.sin(rad),
    };
}

function arcSlice(hue, startOffset, endOffset, innerR, outerR) {
    const startRad = ((hue + startOffset - 90) * Math.PI) / 180;
    const endRad = ((hue + endOffset - 90) * Math.PI) / 180;
    const x1 = WHEEL_CENTER + outerR * Math.cos(startRad);
    const y1 = WHEEL_CENTER + outerR * Math.sin(startRad);
    const x2 = WHEEL_CENTER + outerR * Math.cos(endRad);
    const y2 = WHEEL_CENTER + outerR * Math.sin(endRad);
    const x3 = WHEEL_CENTER + innerR * Math.cos(endRad);
    const y3 = WHEEL_CENTER + innerR * Math.sin(endRad);
    const x4 = WHEEL_CENTER + innerR * Math.cos(startRad);
    const y4 = WHEEL_CENTER + innerR * Math.sin(startRad);
    return `M ${x1} ${y1} A ${outerR} ${outerR} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 0 0 ${x4} ${y4} Z`;
}

const TravelColourWheel = () => {
    const sliceWidth = 14; // ±14° around each hue centre

    return (
        <div className="travel-wheel-block">
            <div className="travel-wheel-canvas">
                <svg width={WHEEL_SIZE} height={WHEEL_SIZE} viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`}>
                    <circle
                        cx={WHEEL_CENTER}
                        cy={WHEEL_CENTER}
                        r={(WHEEL_OUTER_R + WHEEL_INNER_R) / 2}
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth={WHEEL_OUTER_R - WHEEL_INNER_R}
                    />

                    {EXISTING_TRAVEL_COLOURS.map(c => (
                        <path
                            key={c.hex}
                            d={arcSlice(c.hue, -sliceWidth, sliceWidth, WHEEL_INNER_R, WHEEL_OUTER_R)}
                            fill={c.hex}
                            stroke="white"
                            strokeWidth="1"
                        />
                    ))}

                    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(h => {
                        const p = hueToXY(h, WHEEL_OUTER_R + 18);
                        return (
                            <text
                                key={h}
                                x={p.x}
                                y={p.y}
                                fontSize="10"
                                fill="#6b7280"
                                textAnchor="middle"
                                dominantBaseline="middle"
                            >
                                {h}°
                            </text>
                        );
                    })}

                    <text
                        x={WHEEL_CENTER}
                        y={WHEEL_CENTER - 6}
                        fontSize="13"
                        fill="#374151"
                        textAnchor="middle"
                        fontWeight="600"
                    >
                        Travel Palette
                    </text>
                    <text
                        x={WHEEL_CENTER}
                        y={WHEEL_CENTER + 12}
                        fontSize="11"
                        fill="#9ca3af"
                        textAnchor="middle"
                    >
                        (hue wheel)
                    </text>
                </svg>
            </div>

            <div className="travel-wheel-strip">
                <h3>Palette (7 colours)</h3>
                <div className="travel-wheel-chips">
                    {EXISTING_TRAVEL_COLOURS.map(c => (
                        <div key={c.hex} className="travel-wheel-chip">
                            <span className="travel-wheel-chip-swatch" style={{ background: c.hex }} />
                            <span className="travel-wheel-chip-meta">
                                <strong>{c.name}</strong>
                                <code>{c.hex}</code>
                                <small>hue {c.hue}°</small>
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// Travel Colour Simulator — runs pickTravelColor over N consecutive
// synthetic trips so you can visually verify the algorithm's behaviour.
// Each trip is 4 days long; spacing between trips is controllable.
// ============================================================
const SIM_TRIP_DURATION_DAYS = 4;
const SIM_TRIP_SPACING_DAYS = 7;
const SIM_WHEEL_SIZE = 420;
const SIM_WHEEL_CENTER = SIM_WHEEL_SIZE / 2;
const SIM_WHEEL_R_OUTER = 180;
const SIM_WHEEL_R_INNER = 120;

const SAMPLE_SIZES = [7, 14, 28, 56];
const MULTI_RUN_COUNT = 50;

function simSliceD(index, total, innerR, outerR) {
    const segDeg = 360 / total;
    // Start at 12 o'clock, go clockwise.
    const startRad = ((index * segDeg) - 90) * Math.PI / 180;
    const endRad = (((index + 1) * segDeg) - 90) * Math.PI / 180;
    const x1 = SIM_WHEEL_CENTER + outerR * Math.cos(startRad);
    const y1 = SIM_WHEEL_CENTER + outerR * Math.sin(startRad);
    const x2 = SIM_WHEEL_CENTER + outerR * Math.cos(endRad);
    const y2 = SIM_WHEEL_CENTER + outerR * Math.sin(endRad);
    const x3 = SIM_WHEEL_CENTER + innerR * Math.cos(endRad);
    const y3 = SIM_WHEEL_CENTER + innerR * Math.sin(endRad);
    const x4 = SIM_WHEEL_CENTER + innerR * Math.cos(startRad);
    const y4 = SIM_WHEEL_CENTER + innerR * Math.sin(startRad);
    const largeArc = segDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerR} ${innerR} 0 ${largeArc} 0 ${x4} ${y4} Z`;
}

function simLabelXY(index, total, radius) {
    const segDeg = 360 / total;
    const midRad = ((index * segDeg) + segDeg / 2 - 90) * Math.PI / 180;
    return {
        x: SIM_WHEEL_CENTER + radius * Math.cos(midRad),
        y: SIM_WHEEL_CENTER + radius * Math.sin(midRad),
    };
}

function runAlgorithm(segments, options) {
    const palette = Object.keys(TRAVEL_COLOR_HUES);
    const start = new Date('2026-06-01T00:00:00Z').getTime();
    const tripMs = SIM_TRIP_DURATION_DAYS * 24 * 60 * 60 * 1000;
    const spacingMs = SIM_TRIP_SPACING_DAYS * 24 * 60 * 60 * 1000;

    const trips = [];
    const firstColor = palette[Math.floor(Math.random() * palette.length)];
    trips.push({
        startDate: new Date(start),
        endDate: new Date(start + tripMs),
        color: firstColor,
        createdAt: new Date(start),
    });

    const result = [firstColor];

    for (let i = 1; i < segments; i++) {
        const tripStart = start + i * spacingMs;
        const newStart = new Date(tripStart);
        const newEnd = new Date(tripStart + tripMs);
        const pick = pickTravelColor(newStart, newEnd, trips, options);
        result.push(pick);
        trips.push({
            startDate: newStart,
            endDate: newEnd,
            color: pick,
            createdAt: new Date(tripStart),
        });
    }
    return result;
}

function countAdjacencyClashes(sequence, families) {
    let hueAdj = 0;
    let famAdj = 0;
    for (let i = 1; i < sequence.length; i++) {
        const a = sequence[i - 1];
        const b = sequence[i];
        if (!a || !b) continue;
        const hA = TRAVEL_COLOR_HUES[a];
        const hB = TRAVEL_COLOR_HUES[b];
        const diff = Math.abs(hA - hB) % 360;
        const hueDist = diff > 180 ? 360 - diff : diff;
        if (hueDist < 25) hueAdj++;
        if (families && families[a] && families[a] === families[b]) famAdj++;
    }
    return { hueAdj, famAdj };
}

const TravelColourSimulator = () => {
    const [segments, setSegments] = useState(28);
    const [vetoDegrees, setVetoDegrees] = useState(DEFAULT_VETO_DEGREES);
    const [proximityDays, setProximityDays] = useState(DEFAULT_PROXIMITY_DAYS);
    const [schemeKey, setSchemeKey] = useState('none');
    const [colors, setColors] = useState([]);
    const [multiRunStats, setMultiRunStats] = useState(null);

    const families = FAMILY_SCHEMES[schemeKey]?.families || {};

    const options = { vetoDegrees, proximityDays, families };

    const runOnce = () => {
        setColors(runAlgorithm(segments, options));
        setMultiRunStats(null);
    };

    const runMany = () => {
        const aggCounts = Object.fromEntries(Object.keys(TRAVEL_COLOR_HUES).map(c => [c, 0]));
        let zeroPickRuns = 0;
        let totalHueAdj = 0;
        let totalFamAdj = 0;
        let worstHueAdj = 0;
        let worstFamAdj = 0;

        for (let r = 0; r < MULTI_RUN_COUNT; r++) {
            const seq = runAlgorithm(segments, options);
            for (const c of seq) aggCounts[c] = (aggCounts[c] || 0) + 1;

            const used = new Set(seq);
            const missing = Object.keys(TRAVEL_COLOR_HUES).filter(c => !used.has(c)).length;
            if (missing > 0) zeroPickRuns++;

            const { hueAdj, famAdj } = countAdjacencyClashes(seq, families);
            totalHueAdj += hueAdj;
            totalFamAdj += famAdj;
            if (hueAdj > worstHueAdj) worstHueAdj = hueAdj;
            if (famAdj > worstFamAdj) worstFamAdj = famAdj;
        }

        const avgCounts = {};
        for (const [c, n] of Object.entries(aggCounts)) {
            avgCounts[c] = (n / MULTI_RUN_COUNT).toFixed(2);
        }

        setMultiRunStats({
            avgCounts,
            zeroPickRuns,
            avgHueAdj: (totalHueAdj / MULTI_RUN_COUNT).toFixed(2),
            avgFamAdj: (totalFamAdj / MULTI_RUN_COUNT).toFixed(2),
            worstHueAdj,
            worstFamAdj,
        });
        setColors(runAlgorithm(segments, options)); // keep a sample wheel filled
    };

    const reset = () => {
        setColors([]);
        setMultiRunStats(null);
    };

    const counts = colors.reduce((acc, c) => {
        if (c) acc[c] = (acc[c] || 0) + 1;
        return acc;
    }, {});

    const displaySegments = colors.length || segments;
    const hasData = colors.length > 0;

    return (
        <div className="travel-wheel-block">
            <div className="travel-sim-layout">
            <div className="travel-sim-left-col">
            <div className="travel-sim-tuner">
                <h3>Tuner</h3>

                <div className="travel-sim-tuner-row">
                    <label className="travel-sim-tuner-label">Family scheme</label>
                    <div className="travel-sim-radio-group">
                        {Object.entries(FAMILY_SCHEMES).map(([key, scheme]) => (
                            <label key={key} className="travel-sim-radio">
                                <input
                                    type="radio"
                                    name="scheme"
                                    value={key}
                                    checked={schemeKey === key}
                                    onChange={() => setSchemeKey(key)}
                                />
                                <span>{scheme.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {schemeKey !== 'none' && (
                    <div className="travel-sim-tuner-row">
                        <label className="travel-sim-tuner-label">Family map</label>
                        <div className="travel-sim-family-map">
                            {Object.entries(families).map(([hex, fam]) => (
                                <div key={hex} className="travel-sim-family-chip">
                                    <span className="travel-sim-family-swatch" style={{ background: hex }} />
                                    <span>{TRAVEL_COLOR_NAMES[hex]}</span>
                                    <em>→ {fam || 'solo'}</em>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="travel-sim-tuner-row">
                    <label className="travel-sim-tuner-label" htmlFor="veto-slider">
                        Hue veto: <strong>{vetoDegrees}°</strong>
                    </label>
                    <input
                        id="veto-slider"
                        type="range"
                        min="0"
                        max="90"
                        step="1"
                        value={vetoDegrees}
                        onChange={e => setVetoDegrees(Number(e.target.value))}
                        className="travel-sim-slider"
                    />
                    <small className="travel-sim-tuner-hint">
                        Blocks any candidate within this many hue degrees of a nearby trip. 0 = off.
                    </small>
                </div>

                <div className="travel-sim-tuner-row">
                    <label className="travel-sim-tuner-label" htmlFor="prox-slider">
                        Proximity window: <strong>±{proximityDays} days</strong>
                    </label>
                    <input
                        id="prox-slider"
                        type="range"
                        min="7"
                        max="60"
                        step="1"
                        value={proximityDays}
                        onChange={e => setProximityDays(Number(e.target.value))}
                        className="travel-sim-slider"
                    />
                    <small className="travel-sim-tuner-hint">
                        How far back/forward to look for nearby trips when applying vetos.
                    </small>
                </div>

                <div className="travel-sim-tuner-row">
                    <label className="travel-sim-tuner-label">Sample size</label>
                    <div className="travel-sim-radio-group">
                        {SAMPLE_SIZES.map(n => (
                            <label key={n} className="travel-sim-radio">
                                <input
                                    type="radio"
                                    name="size"
                                    value={n}
                                    checked={segments === n}
                                    onChange={() => setSegments(n)}
                                />
                                <span>{n} trips</span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            <div className="travel-sim-controls">
                <button className="travel-sim-btn travel-sim-btn--primary" onClick={runOnce}>
                    Simulate {segments} trips
                </button>
                <button className="travel-sim-btn travel-sim-btn--primary" onClick={runMany}>
                    Run {MULTI_RUN_COUNT}×
                </button>
                <button className="travel-sim-btn" onClick={reset}>
                    Reset
                </button>
                <p className="travel-sim-hint">
                    Single run shows one sequence; multi-run averages distribution + adjacency-clash counts
                    across {MULTI_RUN_COUNT} simulations to surface fairness and rare clashes.
                </p>
            </div>
            </div>{/* /.travel-sim-left-col */}

            <div className="travel-sim-canvas-col">
            <div className="travel-wheel-canvas">
                <svg
                    width={SIM_WHEEL_SIZE}
                    height={SIM_WHEEL_SIZE}
                    viewBox={`0 0 ${SIM_WHEEL_SIZE} ${SIM_WHEEL_SIZE}`}
                >
                    <circle
                        cx={SIM_WHEEL_CENTER}
                        cy={SIM_WHEEL_CENTER}
                        r={(SIM_WHEEL_R_OUTER + SIM_WHEEL_R_INNER) / 2}
                        fill="none"
                        stroke="#f3f4f6"
                        strokeWidth={SIM_WHEEL_R_OUTER - SIM_WHEEL_R_INNER}
                    />

                    {Array.from({ length: displaySegments }).map((_, i) => {
                        const color = colors[i] || null;
                        return (
                            <g key={i}>
                                <path
                                    d={simSliceD(i, displaySegments, SIM_WHEEL_R_INNER, SIM_WHEEL_R_OUTER)}
                                    fill={color || '#e5e7eb'}
                                    stroke="white"
                                    strokeWidth="2"
                                />
                                {displaySegments <= 28 && (
                                    <text
                                        {...simLabelXY(
                                            i,
                                            displaySegments,
                                            (SIM_WHEEL_R_OUTER + SIM_WHEEL_R_INNER) / 2
                                        )}
                                        fontSize="13"
                                        fill={color ? 'white' : '#9ca3af'}
                                        fontWeight="600"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                    >
                                        {i + 1}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    <text
                        x={SIM_WHEEL_CENTER}
                        y={SIM_WHEEL_CENTER - 4}
                        fontSize="13"
                        fill="#374151"
                        textAnchor="middle"
                        fontWeight="600"
                    >
                        Simulator
                    </text>
                    <text
                        x={SIM_WHEEL_CENTER}
                        y={SIM_WHEEL_CENTER + 14}
                        fontSize="11"
                        fill="#9ca3af"
                        textAnchor="middle"
                    >
                        {displaySegments} consecutive trips
                    </text>
                </svg>
            </div>
            </div>
            </div>

            {multiRunStats && (
                <div className="travel-wheel-strip">
                    <h3>Multi-run summary — {MULTI_RUN_COUNT} runs of {segments} trips each</h3>
                    <p className="travel-wheel-strip-hint">
                        Avg picks per colour (ideal ≈ {(segments / 7).toFixed(2)}). Lower adjacency
                        clash counts = better — 0 hue clashes is the goal.
                    </p>
                    <div className="travel-sim-stats-grid">
                        <div className="travel-sim-stat">
                            <strong>Runs with missing colour</strong>
                            <span>{multiRunStats.zeroPickRuns} / {MULTI_RUN_COUNT}</span>
                        </div>
                        <div className="travel-sim-stat">
                            <strong>Avg hue clashes per run</strong>
                            <span>{multiRunStats.avgHueAdj}</span>
                            <small>(worst: {multiRunStats.worstHueAdj})</small>
                        </div>
                        {schemeKey !== 'none' && (
                            <div className="travel-sim-stat">
                                <strong>Avg family clashes per run</strong>
                                <span>{multiRunStats.avgFamAdj}</span>
                                <small>(worst: {multiRunStats.worstFamAdj})</small>
                            </div>
                        )}
                    </div>
                    <div className="travel-wheel-chips" style={{ marginTop: '16px' }}>
                        {Object.entries(TRAVEL_COLOR_HUES).map(([hex]) => {
                            const avg = multiRunStats.avgCounts[hex] || '0.00';
                            return (
                                <div key={hex} className="travel-wheel-chip">
                                    <span className="travel-wheel-chip-swatch" style={{ background: hex }} />
                                    <span className="travel-wheel-chip-meta">
                                        <strong>{TRAVEL_COLOR_NAMES[hex]}</strong>
                                        <small>avg {avg} picks</small>
                                        <code>{hex}</code>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {hasData && !multiRunStats && Object.keys(counts).length > 0 && (
                <div className="travel-wheel-strip">
                    <h3>Distribution across the {segments} trips</h3>
                    <p className="travel-wheel-strip-hint">
                        Ideal: each colour ≈ {(segments / 7).toFixed(1)} picks ({segments} / 7).
                    </p>
                    <div className="travel-wheel-chips">
                        {Object.entries(TRAVEL_COLOR_HUES).map(([hex]) => {
                            const c = counts[hex] || 0;
                            return (
                                <div key={hex} className="travel-wheel-chip">
                                    <span className="travel-wheel-chip-swatch" style={{ background: hex }} />
                                    <span className="travel-wheel-chip-meta">
                                        <strong>{TRAVEL_COLOR_NAMES[hex]}</strong>
                                        <small>{c} pick{c === 1 ? '' : 's'}</small>
                                        <code>{hex}</code>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {hasData && !multiRunStats && (
                <div className="travel-wheel-strip">
                    <h3>Pick sequence</h3>
                    <p className="travel-wheel-strip-hint">
                        Reading left-to-right, each card is one trip. Adjacent cards represent trips 7 days
                        apart — they should never share a hue family (with current settings).
                    </p>
                    <div className="travel-sim-sequence">
                        {colors.map((color, i) => (
                            <div
                                key={i}
                                className="travel-wheel-card"
                                style={{
                                    borderColor: color || '#d1d5db',
                                    backgroundColor: color ? `${color}26` : 'transparent',
                                    color: color ? 'var(--dl-color-gray-900)' : 'var(--dl-color-gray-400)',
                                }}
                            >
                                #{i + 1}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormInputShowcase;
